import type { Chart } from 'klinecharts'

/**
 * The index-range contract for klinecharts.
 *
 * lightweight-charts had `timeScale().setVisibleLogicalRange({from, to})`; the
 * Backtest chart's replay loop calls it on EVERY tick, and useBacktestChartState
 * tells its own programmatic scroll apart from real user panning by comparing the
 * echoed range against the one it just asked for (|Δ| < 1). klinecharts has no
 * such setter, so this module reimplements it exactly — all the engine-internals
 * knowledge lives here and nowhere else.
 *
 * Derived from klinecharts 9.8.12 `TimeScaleStore.adjustVisibleRange()`. With
 *   n = bar count, W = main-pane width, b = bar space,
 *   v = W / b (visible bar count),
 *   d = _lastBarRightSideDiffBarCount (bar-widths from the last bar to the right
 *       edge; NEGATIVE when scrolled back into history):
 *
 *   to   = min(round(d + n + 0.5), n)     // EXCLUSIVE
 *   from = max(round(to - v) - 1, 0)      // one extra, partially-clipped bar
 *
 * Inverting for an inclusive [F, T]:
 *   v = T - F      (NOT T - F + 1: `from` carries that extra partial bar)
 *   b = W / v
 *   d = T - n + 0.5
 *
 * The engine clamps d to [-n + min(2,n), v - min(2,n)] and silently ignores a
 * bar space outside [1, 50], so a request wider than the engine allows degrades
 * to the closest achievable view — same as lightweight-charts clamping
 * barSpacing. getIndexRange always reports what was actually achieved.
 */

/** Inclusive index range, matching lightweight-charts' logical-range semantics. */
export interface IndexRange {
  from: number
  to: number
}

/** klinecharts BarSpaceLimitConstants. setBarSpace() no-ops outside this. */
const MIN_BAR_SPACE = 1
const MAX_BAR_SPACE = 50

/**
 * The smallest right-hand pan bound that still admits this module's own writes.
 *
 * setIndexRange pins the live edge at d = +0.5 bars — a POSITIVE offset, because `to` is
 * deliberately one past the last closed bar to reserve the half-bar gap the forming bar
 * is painted into. In Distance role the engine caps setOffsetRightDistance at the right
 * bound, so anything below half a bar at maximum zoom would quietly shrink that gap and
 * paint the forming bar over the last closed one.
 */
export const MIN_RIGHT_PAN_BOUND = MAX_BAR_SPACE / 2

/**
 * The right pan bound a chart asking for `rightPadBars` of live-edge margin needs.
 *
 * The bound is the same cap that applies to this module's own writes (trap 3 on
 * setPanBounds), so a chart that reserves N bars of blank space must raise it or the engine
 * silently clips the margin back to the bound. Bar space is a runtime value and the bound is
 * install-once, so this sizes it at the 50px ceiling: the margin then survives every zoom
 * level. `rightPadBars = 0` gives MIN_RIGHT_PAN_BOUND exactly — the half bar the forming bar
 * needs, and the pre-existing bound.
 *
 * The user inherits the same slack to pan into, which is the honest trade: a chart that
 * deliberately holds blank space right of the newest bar cannot also forbid panning there.
 */
export function rightPanBoundPx(rightPadBars: number): number {
  return MIN_RIGHT_PAN_BOUND + Math.max(0, rightPadBars) * MAX_BAR_SPACE
}

// DomPosition is a const enum in klinecharts' d.ts, which can't be imported as
// a value under isolatedModules — cast the literal once (same as Workbench/types).
type DomPositionArg = Parameters<Chart['getSize']>[1]
const DOM_MAIN = 'main' as DomPositionArg

/** Width of the candle pane's drawing area — the engine's `_totalBarSpace`. */
function mainWidth(chart: Chart): number {
  const size = chart.getSize('candle_pane', DOM_MAIN)
  return size?.width ?? 0
}

/**
 * The currently visible bars as an inclusive range, or null before the engine
 * has any data. `to` is decremented because the engine's `to` is exclusive.
 */
export function getIndexRange(chart: Chart): IndexRange | null {
  const vr = chart.getVisibleRange()
  if (!vr || vr.to <= 0) return null
  return { from: vr.from, to: Math.max(vr.from, vr.to - 1) }
}

/**
 * How many bars fit on screen at the current zoom — the engine's `visibleBarCount`, and
 * exactly the span setIndexRange inverts (`to - from`).
 *
 * READ THIS RATHER THAN DIFFERENCING getIndexRange, which is a bar SHORT whenever the range
 * is clamped. At the live edge `to` saturates at the bar count, and BacktestChart clamps it
 * again to the last CLOSED bar while a forming bar is painted; at the left edge `from`
 * saturates at 0 and the difference measures the history instead of the screen. A follow loop
 * that re-applied either would zoom the chart in a bar per tick, or pin it at "show
 * everything" forever.
 *
 * Self-consistent with setIndexRange by construction: that computes `barSpace = width / span`
 * from this same width, so handing this value back makes its setBarSpace a no-op and the write
 * becomes a pure scroll. It also reports what was ACHIEVED, so a span the engine refused (bar
 * space outside [1, 50]) converges instead of being re-requested forever.
 */
export function getVisibleBarCount(chart: Chart): number | null {
  const width = mainWidth(chart)
  const barSpace = chart.getBarSpace()
  if (!(width > 0) || !(barSpace > 0)) return null
  return Math.max(1, Math.round(width / barSpace))
}

/**
 * Show `range` (inclusive indices). `dataSize` is the engine's current bar count
 * — pass it explicitly so callers can apply a range against data they just set
 * without a second getDataList() round-trip.
 *
 * setOffsetRightDistance is used rather than scrollToDataIndex because it is
 * exact, synchronous, fires no spurious OnScroll action, and works while a
 * scroll lock is held (scrollToDataIndex routes through scroll(), which
 * early-returns when scrolling is disabled).
 *
 * `rightPadBars` holds that many bar widths of blank space right of the newest bar, so a
 * replay's freshly-revealed candle lands clear of the price axis instead of against it. Two
 * things make it safe to add to the offset and nothing else:
 *
 *  - It is applied ONLY at the live edge (`to` at or past the last bar). There, `to` saturates
 *    at the bar count and `from` is derived from the clamped value, so the range the engine
 *    echoes back is still exactly the one that was asked for — which is what the caller's
 *    programmatic-vs-user-pan test compares against (see useBacktestChartState). Off the live
 *    edge the engine would echo a window shifted by the pad, read that as a user pan, and pause
 *    the replay it came from.
 *  - Bar space is left alone, so the pad costs the `rightPadBars` leftmost bars of the
 *    requested window rather than squeezing all of them narrower.
 *
 * The caller must also raise the right pan bound to `rightPanBoundPx(rightPadBars)`, or the
 * engine caps this write back to the old bound — see setPanBounds, trap 3.
 *
 * A `to` further right than one past the last bar is pinned there. Replay frames BEFORE it
 * reveals — the play loop, the step buttons and the scrubber all move the cursor, then ask for a
 * range ending on the bar that will be newest, while the engine still holds the previous reveal.
 * One bar ahead is exactly the half-bar live-edge slot above, which is why a step always framed
 * right. Further ahead is blank space the engine will not keep as asked: it caps the offset at
 * the pan bound (setPanBounds, trap 3), then holds whatever survived IN BARS as updateData
 * appends (ChartStore.addData only adjusts a negative offset), so a scrub — or a play tick that
 * reveals a batch — left the newest candle stranded that far from the edge. Pinned, the appended bars
 * fill in from the right and the newest lands where a one-bar step would put it — at the range
 * the caller asked for, so the echo still matches.
 */
export function setIndexRange(
  chart: Chart,
  range: IndexRange,
  dataSize: number,
  rightPadBars = 0,
  { keepZoom = false }: { keepZoom?: boolean } = {},
): void {
  if (dataSize <= 0) return

  const to = Math.round(range.to)
  const from = Math.round(range.from)
  // v = to - from, floored at 1: a 1-bar range would divide by zero.
  const visibleBars = Math.max(1, to - from)

  // `keepZoom`: a scroll, not a zoom. The range's `to` places the view at the bar space the chart
  // already has, and its `from` goes unused — how many bars that screen holds is the user's
  // business, not the caller's. With fewer bars than a screen, the bars simply sit against the
  // right edge with blank space to their left.
  const width = keepZoom ? 0 : mainWidth(chart)
  if (width > 0) {
    const barSpace = Math.min(MAX_BAR_SPACE, Math.max(MIN_BAR_SPACE, width / visibleBars))
    // Skip a no-op zoom: setBarSpace runs adjustVisibleRange, which emits an
    // intermediate OnVisibleRangeChange. Replay holds the span constant, so
    // this keeps the steady state to a single event per tick.
    if (Math.abs(barSpace - chart.getBarSpace()) > 1e-6) {
      chart.setBarSpace(barSpace)
    }
  }

  // d = to - n + 0.5 lands round(d + n + 0.5) exactly on to + 1, half a bar
  // clear of the rounding boundary in both directions. The pad rides on top of it, at the
  // live edge only — see above for why anywhere else would read as a user pan.
  const pad = to >= dataSize - 1 ? Math.max(0, rightPadBars) : 0
  // Past the data, the bar the caller is waiting on — see above for why no further than one past.
  const edge = Math.min(to, dataSize)
  chart.setOffsetRightDistance((edge - dataSize + 0.5 + pad) * chart.getBarSpace())
}

/**
 * Bound how far the user may pan past either end of the loaded data, in pixels of
 * blank space.
 *
 * By default klinecharts limits panning by BAR COUNT (`_minVisibleBarCount = {left: 2,
 * right: 2}`), which permits `visibleBarCount - 2` bar widths of empty space beyond the
 * newest bar — ~46 bars at the ~48 the app requests for H1. Worse, the app cannot even
 * see it: once overscrolled the reported `to` saturates at the bar count and `from`
 * stops moving, so every further pixel of drag reports an identical range (the truth
 * lives in the discarded `realFrom`/`realTo`). Nothing can snap back what it can't
 * detect, so the bound has to be set in the engine.
 *
 * Three engine behaviours this wraps, each a trap on its own:
 *
 * 1. The scroll-limit role is GLOBAL. Either setMaxOffset*Distance call flips
 *    `_scrollLimitRole` from BarCount to Distance for BOTH edges, so setting only the
 *    right one silently redefines the left as "<= 50px" (the untouched default). Hence
 *    one function that always sets both.
 * 2. The mapping is CROSSED internally (`maxOffsetDistance.right` feeds
 *    `leftMinVisibleBarCount`). Net effect is the intuitive one, and the only one
 *    callers need: `right` caps blank space right of the LAST bar, `left` caps blank
 *    space left of the FIRST bar.
 * 3. In Distance role setOffsetRightDistance gains `Math.min(maxRight, distance)`, which
 *    applies to OUR writes too. `right` must therefore be at least
 *    MIN_RIGHT_PAN_BOUND — see the constant for why.
 *
 * Pixels, not bars, is the right unit: the engine recomputes the limit from the live
 * bar space on every adjust, so a px bound holds at every zoom level.
 *
 * Sets limits only — the engine does not re-adjust on these calls, so apply this before
 * data arrives rather than expecting it to correct a view that is already overscrolled.
 */
export function setPanBounds(chart: Chart, left: number, right: number): void {
  chart.setMaxOffsetLeftDistance(left)
  chart.setMaxOffsetRightDistance(right)
}
